import React from 'react';
import { KeyRound, FileText, HelpCircle } from 'lucide-react';

/**
 * Секция валидации хеша
 */
const ValidationSection = ({
  validationText,
  setValidationText,
  validationHash,
  setValidationHash,
  errors,
  validateResult
}) => {
  return (
    <div className="mb-6">
      {/* Ошибки валидации */}
      {errors.validationGeneral && (
        <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
          {errors.validationGeneral}
        </div>
      )}

      {/* Информация о валидации */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800 mb-5">
        <div className="flex items-start">
          <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Проверка соответствия пароля хешу
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Введите пароль и Bcrypt-хеш, чтобы проверить их соответствие. 
              Это позволит вам узнать, был ли конкретный пароль зашифрован данным хешем.
            </p>
          </div>
        </div>
      </div>

      {/* Поле ввода текста/пароля */}
      <div className="mb-4">
        <div className="flex items-center mb-2">
          <KeyRound className="w-4 h-4 mr-2 text-blue-500" />
          <label 
            htmlFor="validationText" 
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Пароль или текст для проверки
          </label>
        </div>
        
        <div className="relative">
          <input
            type="text"
            id="validationText"
            value={validationText}
            onChange={(e) => setValidationText(e.target.value)}
            placeholder="Введите пароль для проверки"
            className={`form-input w-full ${errors.validationText ? 'input-error' : ''}`}
            aria-invalid={errors.validationText ? "true" : "false"}
          />
        </div>
        
        {errors.validationText && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.validationText}
          </p>
        )}
      </div>

      {/* Поле ввода хеша */}
      <div className="mb-4">
        <div className="flex items-center mb-2">
          <FileText className="w-4 h-4 mr-2 text-blue-500" />
          <label 
            htmlFor="validationHash" 
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Bcrypt-хеш для проверки
          </label>
        </div>
        
        <div className="relative">
          <input
            type="text"
            id="validationHash"
            value={validationHash}
            onChange={(e) => setValidationHash(e.target.value)}
            placeholder="Введите Bcrypt-хеш (начинается с $2a$, $2b$ или $2y$)"
            className={`form-input w-full font-mono ${errors.validationHash ? 'input-error' : ''}`}
            aria-invalid={errors.validationHash ? "true" : "false"}
          />
        </div>
        
        {errors.validationHash && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.validationHash}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Пример правильного формата: $2a$10$N9qo8uLOickgx2ZMRZoMyeIjZAgcfl7p92ldGxad68LJZdL17lhWy
        </p>
      </div>

      {/* Результат валидации */}
      {validateResult && (
        <div className={`mt-5 p-4 rounded-lg border ${
          validateResult.isMatch 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-start">
            {validateResult.isMatch ? (
              <svg className="w-6 h-6 text-green-600 dark:text-green-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-red-600 dark:text-red-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <div>
              <h3 className={`text-lg font-semibold ${
                validateResult.isMatch 
                  ? 'text-green-800 dark:text-green-200' 
                  : 'text-red-800 dark:text-red-200'
              }`}>
                {validateResult.isMatch 
                  ? 'Пароль соответствует хешу!' 
                  : 'Пароль не соответствует хешу.'}
              </h3>
              <p className={`text-sm ${
                validateResult.isMatch 
                  ? 'text-green-700 dark:text-green-300' 
                  : 'text-red-700 dark:text-red-300'
              }`}>
                {validateResult.isMatch 
                  ? 'Проверка прошла успешно. Введённый пароль действительно соответствует данному хешу.'
                  : 'Проверка не прошла. Либо пароль неверный, либо хеш был создан из другого пароля.'}
              </p>
              
              {!validateResult.isValidFormat && (
                <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  Предупреждение: Формат хеша не соответствует стандартному формату Bcrypt. 
                  Результат проверки может быть ненадежным.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationSection;